(ns ui.view.grid-opensearch.event
  (:require [clojure.string :as string]
            [common.opensearch.access :as os-access]
            [common.opensearch.days-since :as days-since]
            [common.opensearch.facet-policy :as facet-policy]
            [common.opensearch.fields :as os-fields]
            [common.opensearch.kanban-sort :as kanban-sort]
            [common.opensearch.numeric-ranges :as numeric-ranges]
            [common.opensearch.totals :as totals]
            [common.opensearch.utils :as os-utils]
            [common.schema.entities.pipeline :as pipeline]
            [common.schema.listing-definition :as listing-definition]
            [common.schema.registry :as registry]
            [common.localization :as loc]
            [common.util :as cu]
            [frontend.logging :as logging]
            [frontend.local-storage :as ls]
            [frontend.state.current-session :as session]
            [frontend.utils :as fu]
            [fx.ajax :as ajax]
            [oops.core :as oops]
            [re-frame.core :as rf]
            [ui.data.query-results.event :as query-results-event]
            [ui.state.session.cofx :as session-cofx]
            [ui.view.grid-opensearch.density :as grid-density]
            [ui.view.grid-opensearch.model :as model]
            [ui.view.grid-opensearch.selection :as selection]
            [ui.view.grid-opensearch.totals :as grid-totals]))

(rf/reg-event-fx
 ::filter-model-updated
 (fn [{db :db} [_ grid-id filter-model]]
   {:db (assoc-in db (model/filters-full-path grid-id) filter-model)}))

(rf/reg-fx
 ::purge-refresh-server-side
 (fn [ag-api]
   (let [opts (clj->js {:purge true})]
     (cond
       (oops/oget+ ag-api "refreshServerSide")
       (oops/ocall! ag-api "refreshServerSide" opts)

       (oops/oget+ ag-api "refreshServerSideStore")
       (oops/ocall! ag-api "refreshServerSideStore" opts)

       :else
       (js/console.warn "AG Grid API has no refreshServerSide* method" ag-api)))))

(rf/reg-event-fx
 ::trigger-query-search
 (fn [{db :db} [_ grid-id]]
   (let [ag-api (get-in db (model/ag-grid-api-full-path grid-id))]
     (if (nil? ag-api)
       {}
       ;; Mark the grid as refreshing so the listing ignores the transient empty
       ;; selection event the purge makes AG Grid emit. Cleared when the rows load
       ;; (`::load-data-ok` / `::load-group-rows-ok`) or fail. The purge itself runs
       ;; in an effect, so the handler stays pure.
       {:db (assoc-in db (model/refreshing?-full-path grid-id) true)
        :fx [[::purge-refresh-server-side ag-api]]}))))

(rf/reg-event-fx
 ::set-external-filters
 (fn [{db :db} [_ grid-id external-filters-by-field-path external-filter-context external-query-text]]
   (let [current-filters (get-in db (model/external-filters-full-path grid-id))
         current-context (get-in db (model/external-filter-context-full-path grid-id))
         current-query-text (get-in db (model/external-query-text-full-path grid-id))
         changed? (or (not= current-filters external-filters-by-field-path)
                      (not= current-context external-filter-context)
                      (not= current-query-text external-query-text))
         updated-db (-> db
                        (assoc-in (model/external-filters-full-path grid-id) (or external-filters-by-field-path {}))
                        (assoc-in (model/external-filter-context-full-path grid-id) (or external-filter-context {}))
                        (assoc-in (model/external-query-text-full-path grid-id) (or external-query-text "")))]
     (cond-> {:db updated-db}
       changed?
       (assoc :fx [[:dispatch [::trigger-query-search grid-id]]])))))

(defn total-hit-count
  [total]
  (cond
    (number? total) total
    (map? total) (or (:value total)
                     (get total "value")
                     0)
    :else 0))

(defn- record-last-query?
  "The grid keeps the query of its top-level rows. The rows of a group, and a
   grid that is already destroyed, do not change it."
  [{:keys [db grid-id group-keys]}]
  (and (empty? group-keys)
       (some? (get-in db (model/ag-grid-api-full-path grid-id)))))

(defn- assoc-source-field-value
  [row source-field value]
  (cond
    (string/starts-with? source-field "labels.")
    (assoc-in row ["labels" (subs source-field (count "labels."))] value)

    (string/starts-with? source-field "flat.")
    (assoc-in row ["flat" (subs source-field (count "flat."))] value)

    (string/starts-with? source-field "original.")
    (assoc-in row ["original" (subs source-field (count "original."))] value)

    :else
    (assoc row source-field value)))

(defn- empty-group-row
  [column-defs group-keys]
  (let [display-field (->> column-defs
                           (remove :hide)
                           first
                           :display-field)
        row {"__empty-group-row" true
             "__empty-group-key" (str "empty:" (string/join "|" group-keys))}]
    (if (string? display-field)
      (assoc-source-field-value row display-field (loc/tr :grid-no-entities-yet))
      row)))

(defn- update-empty-overlay!
  [db grid-id row-count]
  (when-let [ag-api (get-in db (model/ag-grid-api-full-path grid-id))]
    (if (zero? (or row-count 0))
      (oops/ocall! ag-api "showNoRowsOverlay")
      (oops/ocall! ag-api "hideOverlay"))))

(defn next-request
  "`db` with a new request at `request-path`, and its id. The id is new for all the
   grids, so the answer to an older request can be dropped (`current-request?`)."
  [db request-path]
  (let [id (inc (or (get-in db model/request-seq-path) 0))]
    {:db (-> db
             (assoc-in model/request-seq-path id)
             (assoc-in request-path id))
     :id id}))

(defn start-totals-request
  "`db` with a new request of the count and the totals of the grid `grid-id`, and
   its id. The count of the old query goes, so the status bar does not show it
   for the new query while the new count loads."
  [db grid-id]
  (let [{db :db id :id} (next-request db (model/totals-request-full-path grid-id))]
    {:db (assoc-in db (model/row-total-full-path grid-id) nil)
     :id id}))

(defn current-request?
  "True when `id` is the request that the grid waits for at `request-path`."
  [db {:keys [request-path id]}]
  (= id (get-in db request-path)))

(rf/reg-fx
 ::set-total-row
 (fn [{:keys [api row]}]
   ;; The row at the bottom of the grid with the totals of the number columns.
   (when api
     (oops/ocall! api "setGridOption" "pinnedBottomRowData" (clj->js (if row [row] []))))))

(defn- visible-column-defs
  [column-defs]
  (remove :hide column-defs))

(defn total-row-fx
  "The effect that shows the totals `parsed` (see `common.opensearch.totals/parse`)
   in the row at the bottom of the grid. A grid with no number column has no such
   row."
  [{:keys [db grid-id column-defs parsed session-context]}]
  (let [column-defs (visible-column-defs column-defs)
        texts (grid-totals/totals-texts
               {:column-defs       column-defs
                :parsed            parsed
                :default-currency  (:default-currency session-context)
                :number-multiplier (get-in session-context [:tenant :tenant/cur-multiplier])
                :label-col-id      (:colId (or (first (filter :name-column? column-defs))
                                               (first column-defs)))})]
    [::set-total-row {:api (get-in db (model/ag-grid-api-full-path grid-id))
                      :row (when (seq (grid-totals/request-totals column-defs))
                             {grid-totals/totals-key texts})}]))

(rf/reg-event-fx
 ::load-data-ok
 [(rf/inject-cofx ::session-cofx/session-context)]
 (fn [{db :db session-context :session-context} [_ grid-id {:keys [start-row
                                  column-defs
                                  group-keys
                                  load-callback
                                  query-key
                                  request
                                  refresh-event
                                  first-page-id] :as _args} body]]
   (let [responses [body]
         ok-hits (->> responses (keep :hits))
         sorted-hits (->> ok-hits (mapcat :hits))
         total-hits (->> ok-hits (map (comp total-hit-count :total)) (reduce + 0))
         row-data (->> sorted-hits
                       (map-indexed (fn [idx hit]
                                      (let [hit-source (:_source hit)
                                            data (-> hit-source
                                                     (assoc "original.db/id" (js/parseInt (get hit :_id)))
                                                     (assoc "index" (+ start-row idx 1))
                                                     ;; The marks of OpenSearch in the title, for
                                                     ;; a typo that the search found.
                                                     (cond-> (:highlight hit)
                                                       (assoc "__highlight" (:highlight hit))))]
                                        data)))
                       (doall))
         child-group-empty? (and (seq group-keys)
                                 (zero? total-hits))
         returned-row-data (if child-group-empty?
                             [(empty-group-row column-defs group-keys)]
                             (vec row-data))
         returned-row-count (if child-group-empty?
                              1
                              total-hits)
         db* (cond-> db
               (string? query-key)
               (query-results-event/record-result query-key {:source :grid
                                                             :request request
                                                             :refresh-event refresh-event
                                                             :rows (vec row-data)
                                                             :total total-hits
                                                             :raw body
                                                             :result-meta {:grid-id grid-id
                                                                           :start-row start-row
                                                                           :row-count total-hits}}))
         still-mounted? (boolean (get-in db* (model/ag-grid-api-full-path grid-id)))]
     (when still-mounted?
       (load-callback {:rowData  returned-row-data
                       :rowCount returned-row-count})
       (when-not (seq group-keys)
         (update-empty-overlay! db* grid-id total-hits)))
     ;; The first page of a grid that is not grouped has the totals of all its rows.
     ;; The answer to an older first page does not change them.
     (let [first-page? (and (zero? (or start-row 0))
                            (empty? group-keys)
                            (current-request? db* {:request-path (model/totals-request-full-path grid-id)
                                                   :id           first-page-id}))]
       {:db (-> db*
                (cond-> (record-last-query? {:db db*
                                             :grid-id grid-id
                                             :group-keys group-keys})
                  (assoc-in (model/last-query-total-full-path grid-id) total-hits))
                (assoc-in (model/refreshing?-full-path grid-id) false)
                (cond-> first-page?
                  (assoc-in (model/row-total-full-path grid-id) total-hits)))
        :fx (when first-page?
              [(total-row-fx {:db              db*
                              :grid-id         grid-id
                              :column-defs     column-defs
                              :parsed          (totals/parse (grid-totals/request-totals
                                                              (visible-column-defs column-defs))
                                                             (:aggregations body))
                              :session-context session-context})])}))))

(defn- clear-total-row-fx
  "The effect that empties the row of the totals. The totals of the old filters
   must not show while the new ones load, or when they fail."
  [db grid-id]
  [::set-total-row {:api (get-in db (model/ag-grid-api-full-path grid-id))
                    :row nil}])

(rf/reg-event-fx
 ::load-grand-totals
 (fn [_ [_ grid-id {:keys [column-defs id] :as request}]]
   {::ajax/send {:operation  :search/search
                 :body       (-> request
                                 (dissoc :column-defs :id)
                                 (assoc :size 0
                                        :from 0))
                 :timeout-ms 60000
                 :on-ok      [::load-grand-totals-ok grid-id {:id          id
                                                              :totals      (:totals request)
                                                              :column-defs column-defs}]
                 :on-error   [::load-grand-totals-failed grid-id {:id id}]}}))

(rf/reg-event-fx
 ::load-grand-totals-ok
 [(rf/inject-cofx ::session-cofx/session-context)]
 (fn [{db :db session-context :session-context} [_ grid-id {:keys [id totals column-defs]} body]]
   ;; The answer to an older request does nothing: the filters changed since.
   (when (current-request? db {:request-path (model/totals-request-full-path grid-id)
                               :id           id})
     ;; The count of the records that match. The sum of the counts of the groups
     ;; is not it: a record can be in two groups, and the groups have a limit.
     {:db (assoc-in db (model/row-total-full-path grid-id) (total-hit-count (get-in body [:hits :total])))
      :fx [(total-row-fx {:db              db
                          :grid-id         grid-id
                          :column-defs     column-defs
                          :parsed          (totals/parse totals (:aggregations body))
                          :session-context session-context})]})))

(rf/reg-event-fx
 ::load-grand-totals-failed
 (fn [{db :db} [_ grid-id {:keys [id]} err]]
   ;; The rows still show. The row of the totals and the count stay empty, as
   ;; `start-totals-request` left them.
   (when (current-request? db {:request-path (model/totals-request-full-path grid-id)
                               :id           id})
     (logging/warn "Failed to load the totals of the grid" err))
   {}))

(rf/reg-event-fx
 ::load-data-failed
 (fn [{db :db} [_ {:keys [fail-js query-key grid-id] :as _args} err]]
   (js/console.warn "Failed to load grid data" err)
   (fail-js)
   {:db (cond-> db
          (string? query-key)
          (query-results-event/record-error query-key err)

          (some? grid-id)
          (assoc-in (model/refreshing?-full-path grid-id) false))}))

(declare set-filter->os-clause
         resolve-filter-field)

(defn- boolean-data-type?
  [data-type]
  (= data-type :data.type/boolean))

(def unknown-filter-option-value :filter.option/unknown)
(def unknown-group-key "filter.option/unknown")
(def known-filter-option-value :filter.option/known)
(def known-group-key "filter.option/known")
(def uncategorized-group-label "Uncategorized")

(defn- filter-option?
  "The values of the \"Is known\" and \"Is unknown\" options reach the filter in
   three forms. The backend sends a keyword. A row group key is the string without
   the colon. The set filter of AG Grid keeps the keyword as `pr-str`, which is
   the string with the colon."
  [option-value option-key value]
  (or (= value option-value)
      (= value option-key)
      (= value (str option-value))))

(defn- unknown-filter-option?
  [value]
  (filter-option? unknown-filter-option-value unknown-group-key value))

(defn- known-filter-option?
  [value]
  (filter-option? known-filter-option-value known-group-key value))

(defn- normalized-group-key
  [value]
  (cond
    (keyword? value)
    (if-let [ns (namespace value)]
      (str ns "/" (name value))
      (name value))

    :else value))

(defn- group-bucket-display-label
  [data-type {:keys [value label]}]
  (if (unknown-filter-option? value)
    uncategorized-group-label
    (or label
        (case data-type
          :data.type/datetime (when (some? value)
                                (fu/display-datetime-timestamp value))
          :data.type/date (when (some? value)
                            (fu/display-date-timestamp value))
          nil)
        (str value))))

(defn- group-bucket-filter-value
  [data-type value {:keys [from to]}]
  (if (unknown-filter-option? value)
    unknown-filter-option-value
    (if (or (some? from)
            (some? to))
      {:type (if (contains? facet-policy/numeric-data-types data-type)
               :numeric-percentile-range
               :range)
       :from from
       :to to}
      (normalized-group-key value))))

(defn- range-bucket?
  [{:keys [from to]}]
  (or (some? from)
      (some? to)))

(defn- group-bucket-key
  [value bucket]
  (let [normalized-value (normalized-group-key value)]
    (if (range-bucket? bucket)
      (str "range:" normalized-value ":" (:from bucket) ":" (:to bucket))
      normalized-value)))

(defn- range-group-filter?
  [value]
  (and (map? value)
       (contains? #{:range
                    :date-calendar-day
                    :numeric-percentile-range}
                  (:type value))))

(defn- range-group->os-clause
  [field {:keys [from to type]}]
  (if (= :numeric-percentile-range type)
    (numeric-ranges/range-clause field {:from from :to to})
    (cond
      (and (some? from)
           (some? to))
      {:range {field {:gte from
                      :lte to}}}

      (some? from)
      {:range {field {:gte from}}}

      (some? to)
      {:range {field {:lte to}}}

      :else
      nil)))

(defn- coerce-group-key-value
  [data-type value]
  (cond
    (and (boolean-data-type? data-type)
         (string? value))
    (case (string/lower-case value)
      "true" true
      "false" false
      value)

    :else
    value))

(defn- row-group-col-alias
  [row-group-col]
  (or (:id row-group-col)
      (:field row-group-col)))

(defn- row-group-column-def-by-alias
  [column-defs]
  (->> column-defs
       (keep (fn [column-def]
               (when-let [alias (:colId column-def)]
                 [alias column-def])))
       (into {})))

(defn- row-group-field-paths
  [row-group-cols column-defs]
  (let [column-def-by-alias (row-group-column-def-by-alias column-defs)]
    (->> row-group-cols
         (keep (fn [row-group-col]
                 (let [alias (row-group-col-alias row-group-col)
                       column-def (get column-def-by-alias alias)]
                   (or (:field-path column-def)
                       (:field column-def)))))
         distinct
         vec)))

(defn- row-group-parent-clauses
  [row-group-cols group-keys column-defs group-value-mapping]
  (let [column-def-by-alias (row-group-column-def-by-alias column-defs)]
    (->> group-keys
         (map-indexed (fn [idx raw-group-key]
                        (when-let [row-group-col (nth row-group-cols idx nil)]
                          (let [alias (row-group-col-alias row-group-col)
                                column-def (get column-def-by-alias alias)
                                field-str (:field-str column-def)
                                filter-field (resolve-filter-field field-str)
                                data-type (:field-data-type column-def)
                                mapped-group-key (get-in group-value-mapping [alias raw-group-key] raw-group-key)
                                group-key (coerce-group-key-value data-type mapped-group-key)]
                            (when (and filter-field (some? group-key))
                              (if (range-group-filter? group-key)
                                (range-group->os-clause filter-field group-key)
                                (set-filter->os-clause filter-field {:values [group-key]})))))))
         (remove nil?)
         vec)))

(defn- active-row-group-column-def
  [row-group-cols group-keys column-defs]
  (when-let [row-group-col (nth row-group-cols (count group-keys) nil)]
    (let [alias (row-group-col-alias row-group-col)
          column-def (get (row-group-column-def-by-alias column-defs) alias)]
      column-def)))

(defn- active-row-group-field-path
  [row-group-cols group-keys column-defs]
  (when-let [column-def (active-row-group-column-def row-group-cols group-keys column-defs)]
    (or (:field-path column-def)
        (:field column-def))))

(defn- group-column-facet-mode
  [column-def]
  (let [data-type (:field-data-type column-def)]
    (cond
      (contains? #{:data.type/date
                   :data.type/datetime}
                 data-type)
      :date-calendar-days

      (contains? facet-policy/numeric-data-types data-type)
      :numeric-percentile-ranges

      :else
      :terms)))

(defn- visible-group-buckets
  [hide-empty-columns? buckets]
  (->> (or buckets [])
       (remove (fn [{:keys [value]}]
                 (= value :filter.option/known)))
       (remove (fn [{:keys [value count]}]
                 (and (= value :filter.option/unknown)
                      (<= (or count 0) 0))))
       (remove (fn [{:keys [count]}]
                 (and hide-empty-columns?
                      (<= (or count 0) 0))))
       vec))

(defn- numeric-bucket-sort-value
  [bucket]
  (let [{:keys [from to value]} bucket
        parsed (cond
                 (unknown-filter-option? value) js/Number.POSITIVE_INFINITY
                 (some? from) from
                 (some? to) js/Number.NEGATIVE_INFINITY
                 (number? value) value
                 (nil? value) js/Number.POSITIVE_INFINITY
                 :else (js/parseFloat (str value)))]
    (if (js/isNaN parsed)
      js/Number.POSITIVE_INFINITY
      parsed)))

(defn- numeric-bucket-desc-sort-key
  [bucket]
  (let [value (numeric-bucket-sort-value bucket)
        missing? (= value js/Number.POSITIVE_INFINITY)]
    [(if missing? 1 0)
     (if missing? 0 (- value))]))

(defn- bucket-label-sort-value
  [group-data-type bucket]
  (string/lower-case (str (group-bucket-display-label group-data-type bucket))))

(defn- sort-group-buckets
  [{:keys [buckets group-data-type sort-mode]}]
  (case (kanban-sort/normalize-row-sort-mode sort-mode)
    :sort-by/alphanumerical
    (vec (sort-by (fn [bucket]
                    (bucket-label-sort-value group-data-type bucket))
                  buckets))

    :sort-by/value-desc
    (vec (sort-by (fn [bucket]
                    (bucket-label-sort-value group-data-type bucket))
                  #(compare %2 %1)
                  buckets))

    :sort-by/most-hits
    (vec (sort-by (fn [{:keys [count]}]
                    (long (or count 0)))
                  >
                  buckets))

    :sort-by/least-hits
    (vec (sort-by (fn [{:keys [count]}]
                    (long (or count 0)))
                  buckets))

    :sort-by/numeric
    (vec (sort-by numeric-bucket-sort-value buckets))

    :sort-by/numeric-desc
    (vec (sort-by numeric-bucket-desc-sort-key buckets))

    buckets))

(defn- stage-group-field?
  "True when `field-path` is the stage field of one of the four pipeline types."
  [field-path]
  (pipeline/stage-attribute? (if (sequential? field-path)
                               (last field-path)
                               field-path)))

(defn- sort-buckets-by-stage-ord
  "Order the buckets of a stage field by the `:common/ord` of the stage entity,
   the order of the stages in the pipeline. The value of a bucket is the id of the
   stage. A bucket with no stage entity, as the bucket of the rows that have no
   stage, goes last. Buckets that are equal keep the order that they had."
  [{:keys [buckets entities]}]
  (vec (sort-by (fn [{:keys [value]}]
                  (or (when (number? value)
                        (:common/ord (get entities value)))
                      js/Number.MAX_SAFE_INTEGER))
                buckets)))

(defn group-buckets
  "The groups of one level of the grid, in the order that the grid shows them.
   The groups of a stage field follow the order of the stages in the pipeline, as
   the columns of the Kanban board do. The sort mode of the user does not apply to
   them, and the Rows by panel says so. The groups of any other field follow the
   sort mode `sort-mode`. `entities` is the map of the session entities, by id."
  [{:keys [buckets hide-empty-columns? group-data-type group-field-path sort-mode entities]}]
  (let [visible-buckets (visible-group-buckets hide-empty-columns? buckets)]
    (if (stage-group-field? group-field-path)
      (sort-buckets-by-stage-ord {:buckets  visible-buckets
                                  :entities entities})
      (sort-group-buckets {:buckets         visible-buckets
                           :group-data-type group-data-type
                           :sort-mode       sort-mode}))))

(rf/reg-event-fx
 ::load-group-rows-ok
 [(rf/inject-cofx ::session-cofx/session-context)]
 (fn [{db :db session-context :session-context} [_ grid-id {:keys [start-row end-row load-callback column-defs row-group-cols group-keys
                                                                   hide-empty-columns? row-group-sort-mode query-key request refresh-event] :as _args} group-field-path resp]]
   (let [alias (os-utils/field-path->alias group-field-path)
         label-key (str alias "_en")
         group-column-def (active-row-group-column-def row-group-cols group-keys column-defs)
         group-data-type (:field-data-type group-column-def)
         buckets (group-buckets {:buckets             (get-in resp [:body :buckets])
                                 :hide-empty-columns? hide-empty-columns?
                                 :group-data-type     group-data-type
                                 :group-field-path    group-field-path
                                 :sort-mode           row-group-sort-mode
                                 :entities            (:entities session-context)})
         group-value-mapping (->> buckets
                                  (mapcat (fn [{:keys [value] :as bucket}]
                                            (let [normalized-value (normalized-group-key value)
                                                  group-key (group-bucket-key value bucket)
                                                  group-label (group-bucket-display-label group-data-type bucket)
                                                  filter-value (group-bucket-filter-value group-data-type value bucket)]
                                              [[group-label filter-value]
                                               [normalized-value filter-value]
                                               [group-key filter-value]
                                               [(str group-key) filter-value]])))
                                  (into {}))
         total-buckets (count buckets)
         from-idx (max 0 (or start-row 0))
         to-idx (min total-buckets (or end-row total-buckets))
         page-buckets (if (< from-idx to-idx)
                        (subvec buckets from-idx to-idx)
                        [])
         total-texts (fn [bucket]
                       (grid-totals/totals-texts
                        {:column-defs       (visible-column-defs column-defs)
                         :parsed            (:totals bucket)
                         :default-currency  (:default-currency session-context)
                         :number-multiplier (get-in session-context [:tenant :tenant/cur-multiplier])}))
         row-data (->> page-buckets
                       (mapv (fn [{:keys [value count] :as bucket}]
                               (let [group-key (group-bucket-key value bucket)
                                     group-label (group-bucket-display-label group-data-type bucket)]
                                 {"childCount" count
                                  "__group-key" group-key
                                  "groupLabel" group-label
                                  "flat" {alias group-key}
                                  "labels" {label-key group-label}
                                  ;; The totals of the group, in its number columns.
                                  grid-totals/totals-key (total-texts bucket)}))))
         updated-db (assoc-in db (model/group-value-mapping-full-path grid-id alias) group-value-mapping)
         updated-db* (cond-> updated-db
                       (string? query-key)
                       (query-results-event/record-result query-key {:source :grid-grouped
                                                                     :request request
                                                                     :refresh-event refresh-event
                                                                     :rows row-data
                                                                     :total total-buckets
                                                                     :facets buckets
                                                                     :raw resp
                                                                     :result-meta {:grid-id grid-id
                                                                                   :start-row from-idx
                                                                                   :end-row to-idx}}))
         still-mounted? (boolean (get-in updated-db* (model/ag-grid-api-full-path grid-id)))]
     (when still-mounted?
       (load-callback {:rowData row-data
                       :rowCount total-buckets})
       (update-empty-overlay! updated-db* grid-id total-buckets))
     {:db (assoc-in updated-db* (model/refreshing?-full-path grid-id) false)})))

(defn- technical-or-derived-field-path?
  [field-path]
  (let [segments (when (sequential? field-path) (vec field-path))
        leaf (last segments)]
    (or (and (= 1 (count segments))
             (= leaf :common/display-name))
        (and (< 1 (count segments))
             (contains? #{:db/id :entity/type :common/display-name :common/tenant} leaf)))))

(defn request-field-paths
  "The field paths that the grid asks the search for: the field of each visible
   column, and for a visible currency column the field that holds the currency of
   its values, so that a cell shows the currency of its row. The search throws for
   a field that the user cannot read, so the currency field is asked for only when
   its path is in `accessible-field-paths`. A column def has the path of its
   currency field as `:currency-field-path`."
  [{:keys [column-defs accessible-field-paths]}]
  (let [accessible (into #{}
                         (map (fn [field-path]
                                (if (keyword? field-path)
                                  [field-path]
                                  (vec field-path))))
                         accessible-field-paths)]
    (->> (or column-defs [])
         (remove :hide)
         (mapcat (fn [{:keys [field-path currency-field-path]}]
                   [field-path
                    (when (contains? accessible currency-field-path)
                      currency-field-path)]))
         (remove nil?)
         distinct
         vec)))

(defn visible-request-field-paths
  "The field paths of the visible columns, with no currency field."
  [column-defs]
  (request-field-paths {:column-defs column-defs}))

(defn- text-filter-fields
  [field]
  (let [keyword-field (if (string/ends-with? field ".keyword")
                        field
                        (str field ".keyword"))
        raw-field (if (string/ends-with? field ".keyword")
                    (string/replace field #"\\.keyword$" "")
                    field)]
    {:keyword-field keyword-field
     :raw-field raw-field}))

(defn- escape-wildcard-value
  [value]
  (-> value
      (string/replace "\\" "\\\\")
      (string/replace "*" "\\*")
      (string/replace "?" "\\?")))

(defn- should-clause
  [clauses]
  (let [clauses (->> clauses
                     (remove nil?)
                     vec)]
    (cond
      (empty? clauses) nil
      (= 1 (count clauses)) (first clauses)
      :else {:bool {:should clauses
                    :minimum_should_match 1}})))

(defn- wildcard-any
  [fields pattern]
  (should-clause
   (map (fn [target-field]
          {:wildcard {target-field {:value pattern
                                    :case_insensitive true}}})
        fields)))

(defn- keyword-equals-clause
  [keyword-field value]
  {:term {keyword-field value}})

(defn text-filter->os-clause
  "Build a single OpenSearch clause for ag-grid text filter.

  `field`     – OS field name (string), e.g. \"flat.last-name\"
  `flt`       – one column's filter model from ag-grid (keywordized keys).
               Expected keys: :type, :filter.

  Supports types:
  \"contains\", \"notContains\", \"equals\", \"notEqual\",
  \"startsWith\", \"endsWith\", \"blank\", \"notBlank\"."
  [field {:keys [type filter] :as ag-filter}]
  (let [{:keys [keyword-field raw-field]} (text-filter-fields field)
        exists-field raw-field
        escaped-filter (some-> filter escape-wildcard-value)
        ;; The wildcard on the keyword field finds any part of a short text. The
        ;; keyword field has no value for a text that is longer than its
        ;; `ignore_above` (256 characters), so the phrase on the text field finds
        ;; such a text too. The Filter by of the toolbar does the same (see
        ;; `common.opensearch.filter-queries/contains-query-clause`).
        contains-clause (when-not (cu/blank-value? escaped-filter)
                          (should-clause
                           [(wildcard-any [keyword-field]
                                          (str "*" escaped-filter "*"))
                            {:match_phrase {raw-field (string/trim filter)}}]))]
    (case type
      "contains"
      contains-clause

      "notContains"
      (when contains-clause
        {:bool {:must_not [contains-clause]}})

      ;; exact equality for text fields.
      "equals"
      (when-not (cu/blank-value? filter)
        (keyword-equals-clause keyword-field filter))

      "notEqual"
      (when-not (cu/blank-value? filter)
        (when-let [equals-clause (keyword-equals-clause keyword-field filter)]
          {:bool {:must_not [equals-clause]}}))

      ;; begins/ends with on keyword field.
      "startsWith"
      (when-not (cu/blank-value? escaped-filter)
        (wildcard-any [keyword-field]
                      (str escaped-filter "*")))

      "endsWith"
      (when-not (cu/blank-value? escaped-filter)
        (wildcard-any [keyword-field]
                      (str "*" escaped-filter)))

      ;; blank / notBlank
      ;; blank  -> field missing OR empty string
      "blank"
      {:bool {:should               [{:bool {:must_not [{:exists {:field exists-field}}]}}
                                     {:term {keyword-field ""}}]
              :minimum_should_match 1}}

      ;; notBlank -> field exists AND not empty string
      "notBlank"
      {:bool {:must [{:exists {:field exists-field}}
                     {:bool {:must_not [{:term {keyword-field ""}}]}}]}}

      ;; default / unsupported type
      (do
        (logging/error "Unsupported text filter type " type " for field " field " filter " ag-filter)
        nil))))

(defn- parse-number-filter-value
  [value]
  (cond
    (number? value) value
    (string? value) (let [parsed (js/parseFloat value)]
                      (when-not (js/Number.isNaN parsed)
                        parsed))
    :else nil))

(defn- value-number-filter->os-clause
  "Build a single OpenSearch clause for ag-grid number filter on a field that holds
   the number."
  [field {:keys [type filter filterTo] :as ag-filter}]
  (let [value (parse-number-filter-value filter)
        value-to (parse-number-filter-value filterTo)]
    (case type
      "equals"
      (when (some? value)
        {:term {field value}})

      "notEqual"
      (when (some? value)
        {:bool {:must     [{:exists {:field field}}]
                :must_not [{:term {field value}}]}})

      "lessThan"
      (when (some? value)
        {:range {field {:lt value}}})

      "lessThanOrEqual"
      (when (some? value)
        {:range {field {:lte value}}})

      "greaterThan"
      (when (some? value)
        {:range {field {:gt value}}})

      "greaterThanOrEqual"
      (when (some? value)
        {:range {field {:gte value}}})

      "inRange"
      (when (and (some? value) (some? value-to))
        {:range {field {:gte value
                        :lte value-to}}})

      "blank"
      {:bool {:must_not [{:exists {:field field}}]}}

      "notBlank"
      {:exists {:field field}}

      (do
        (logging/error "Unsupported number filter type " type " for field " field " filter " ag-filter)
        nil))))

(def ^:private days-operator-by-filter-type
  {"equals"             :eq
   "lessThan"           :lt
   "lessThanOrEqual"    :lte
   "greaterThan"        :gt
   "greaterThanOrEqual" :gte
   "inRange"            :between})

(defn- days-filter->os-clause
  "The clause of a number filter on a field that shows the days since a time. The
   index holds the start time in `since-field`, see `common.opensearch.days-since`."
  [since-field {:keys [type filter filterTo]}]
  (let [value (parse-number-filter-value filter)
        value-to (parse-number-filter-value filterTo)]
    (case type
      "notEqual"
      (when-let [clause (days-since/range-clause since-field {:operator :eq
                                                              :value    value})]
        {:bool {:must     [{:exists {:field since-field}}]
                :must_not [clause]}})

      "blank"
      {:bool {:must_not [{:exists {:field since-field}}]}}

      "notBlank"
      {:exists {:field since-field}}

      (days-since/range-clause since-field {:operator (get days-operator-by-filter-type type)
                                            :value    value
                                            :value-to value-to}))))

(defn number-filter->os-clause
  "Build a single OpenSearch clause for ag-grid number filter."
  [field ag-filter]
  (if-let [since-field (days-since/since-alias field)]
    (days-filter->os-clause since-field ag-filter)
    (value-number-filter->os-clause field ag-filter)))

(defn date-filter->os-clause
  "Build a single OpenSearch clause for ag-grid date filter.

  AG Grid date filter model typically looks like:
  {:filterType \"date\"
   :type \"equals\"|\"notEqual\"|\"lessThan\"|\"greaterThan\"|\"inRange\"|\"blank\"|\"notBlank\"
   :dateFrom \"YYYY-MM-DD\" ; or ISO-ish string
   :dateTo   \"YYYY-MM-DD\" ; only for inRange
   ...}

  This implementation assumes the indexed value in `field` is an epoch millis (number).

  Semantics:
  - equals:    within the calendar day (inclusive)
  - notEqual:  not within the calendar day
  - lessThan:  strictly before the day
  - greaterThan: strictly after the day
  - inRange:   within [from-day, to-day] inclusive
  - blank:     missing
  - notBlank:  exists

  Notes:
  - The picked day is read from the text as a calendar day. AG Grid gives it as
    \"YYYY-MM-DD HH:mm:ss\" with no time zone, and a parse of that text as a
    `js/Date` uses the time zone of the browser, which moved the day.
  - The day ranges use `gte`/`lt`. A date column shows the day in UTC, so its
    range is the day in UTC. A datetime column shows the day in the time zone
    of the browser, so its filter has `:local-day? true` (see
    `mark-local-day-date-filters`), and its range is the day in that time zone.
  - If `field` ends with `.keyword`, we strip it for `exists` checks.
  "
  [field {:keys [type dateFrom dateTo local-day?] :as ag-filter}]
  (let [calendar-day (fn [s]
                       (when-let [[_ year month day] (and (string? s)
                                                          (re-find #"^(\d{4})-(\d{2})-(\d{2})" s))]
                         [(js/parseInt year 10)
                          (dec (js/parseInt month 10))
                          (js/parseInt day 10)]))
        ;; The start of the calendar day `[year month day]`, plus `days` days.
        day-start-ms (fn [[year month day] days]
                       (if local-day?
                         (.getTime (js/Date. year month (+ day days)))
                         (js/Date.UTC year month (+ day days))))
        from-day (calendar-day dateFrom)
        to-day (calendar-day dateTo)
        from-start (some-> from-day (day-start-ms 0))
        from-next (some-> from-day (day-start-ms 1))
        to-next (some-> to-day (day-start-ms 1))
        exists-field (if (string/ends-with? field ".keyword")
                       (string/replace field #"\\.keyword$" "")
                       field)]
    (case type
      ;; blank / notBlank
      "blank"
      {:bool {:must_not [{:exists {:field exists-field}}]}}

      "notBlank"
      {:exists {:field exists-field}}

      ;; equals / notEqual are day-range checks
      "equals"
      (do
        (when-not from-start
          (logging/error "Date filter requires :dateFrom " {:field field :filter ag-filter}))
        (when from-start
          {:range {field {:gte from-start
                          :lt  from-next}}}))

      "notEqual"
      (do
        (when-not from-start
          (logging/error "Date filter requires :dateFrom " {:field field :filter ag-filter}))
        (when from-start
          {:bool {:must_not [{:range {field {:gte from-start
                                             :lt  from-next}}}]}}))

      ;; lessThan: strictly before the day (lt day-start)
      "lessThan"
      (do
        (when-not from-start
          (logging/error "Date filter requires :dateFrom " {:field field :filter ag-filter}))
        (when from-start
          {:range {field {:lt from-start}}}))

      ;; greaterThan: strictly after the day (gte next-day-start)
      "greaterThan"
      (do
        (when-not from-next
          (logging/error "Date filter requires :dateFrom " {:field field :filter ag-filter}))
        (when from-next
          {:range {field {:gte from-next}}}))

      ;; inRange: inclusive days from..to
      "inRange"
      (do
        (when-not (and from-start to-next)
          (logging/error "Date range filter requires :dateFrom and :dateTo " {:field field :filter ag-filter}))
        (when (and from-start to-next)
          {:range {field {:gte from-start
                          :lt  to-next}}}))

      ;; default / unsupported type
      (do
        (logging/error "Unsupported date filter type " type " for field " field " filter " ag-filter)
        nil))))

(defn- original-field->flat-alias
  "Translate a legacy `original.<ns>/<name>` filter key to the flat alias the
   value is actually queryable under, or nil when it cannot be translated.

   Saved views persisted by the legacy grid store keys in this shape (built by
   `cos/field->os-field`). `original.*` is deliberately unmapped on dedicated
   indexes - it exists for `_source` display only - so sending one as a filter
   matches zero documents instead of erroring. See
   ai/opensearch-document-model.md."
  [field]
  (let [attribute (subs field (count "original."))]
    ;; A nested `parent.original.child` form has no single flat alias - only a
    ;; plain `original.<attr>` maps cleanly.
    (when-not (string/includes? attribute ".")
      (os-utils/field-path->flat-alias (keyword attribute)))))

(defn- resolve-filter-field
  [field]
  (if (string? field)
    (cond
      (or (string/starts-with? field "flat.")
          (string/starts-with? field "labels."))
      field

      (string/starts-with? field "original.")
      (original-field->flat-alias field)

      :else
      (os-utils/field-path->flat-alias field))
    (os-utils/field-path->flat-alias field)))

(defn- canonical-filter-alias
  [field]
  (let [resolved (or (resolve-filter-field field)
                     (when (string? field) field))
        resolved (if (and (string? resolved)
                          (string/ends-with? resolved ".keyword"))
                   (subs resolved 0 (- (count resolved) (count ".keyword")))
                   resolved)]
    (cond
      (string/starts-with? resolved "flat.")
      (subs resolved (count "flat."))

      (string/starts-with? resolved "labels.")
      (let [s (subs resolved (count "labels."))]
        (string/replace s #"_([a-z]{2}|[a-z]{2}-[A-Z]{2})$" ""))

      (string/starts-with? resolved "original.")
      (subs resolved (count "original."))

      :else
      resolved)))

(defn- without-target-column-filter
  [filter-model target-field]
  (let [target-canonical (canonical-filter-alias target-field)]
    (->> (or filter-model {})
         (remove (fn [[field _]]
                   (= (canonical-filter-alias field) target-canonical)))
         (into {}))))

(defn- simple-filter-model?
  "True for the model of one column filter. A text, number or date filter has a
   :type, for example \"contains\". A set filter has no :type, only :values."
  [fm]
  (and (map? fm)
       (contains? fm :filterType)
       (or (contains? fm :type)
           (= "set" (:filterType fm)))))

(defn- combined-filter-model?
  [fm]
  (and (map? fm)
       (contains? fm :operator)
       (or (contains? fm :condition1)
           (contains? fm :condition2)
           (sequential? (:conditions fm)))))

(defn- merge-conditions-clause
  [operator condition-clauses]
  (let [condition-clauses (->> condition-clauses
                               (remove nil?)
                               vec)
        operator (-> (or operator "AND")
                     str
                     string/upper-case)]
    (when (seq condition-clauses)
      (if (= operator "OR")
        {:bool {:should               condition-clauses
                :minimum_should_match 1}}
        {:bool {:must condition-clauses}}))))

(defn- column-filter->os-clause
  [field {:keys [filterType operator conditions condition1 condition2] :as fm}]
  (cond
    (combined-filter-model? fm)
    (let [raw-conditions (or conditions [condition1 condition2])
          normalized-conditions (->> raw-conditions
                                     (remove nil?)
                                     (mapv (fn [condition]
                                             (cond-> (assoc condition :filterType (or (:filterType condition)
                                                                                      filterType))
                                               (:local-day? fm)
                                               (assoc :local-day? true)))))]
      (merge-conditions-clause operator
                               (map (fn [condition]
                                      (column-filter->os-clause field condition))
                                    normalized-conditions)))

    (simple-filter-model? fm)
    (case filterType
      "text" (text-filter->os-clause field fm)
      "number" (number-filter->os-clause field fm)
      "set" (set-filter->os-clause field fm)
      "date" (date-filter->os-clause field fm)
      (do
        (logging/error "Unsupported filter type " filterType " for field " field " filter " fm)
        nil))

    :else
    nil))

(defn set-filter->os-clause
  "Build an OpenSearch clause for ag-grid set filter.

  AG Grid set filter model typically looks like:
  {<colId> {:filterType \"set\" :values [...]}}

  The selected options are alternatives, so the clause matches a row that has
  any of them:
  - `terms` query for the selected data values
  - `exists` for the option \"Is known\"
  - a missing field for the option \"Is unknown\", or a nil value (a blank)
  Both \"Is known\" and \"Is unknown\" match every row, so they give no clause.

  Notes:
  - For string/keyword values we default to using a `.keyword` field unless
    the field already ends with `.keyword`. The two options are not data values,
    so they do not make a numeric field, such as the id of a reference, use it.
  - For boolean/number values we keep the field as-is.
  - If no values are selected, returns nil (caller should ignore it)."
  [field {:keys [values]}]
  (let [unwrap-value (fn [v]
                       ;; AG Grid may pass complex objects; we filter by the stable `:value`.
                       (if (map? v)
                         (or (:value v)
                             (get v "value")
                             (get v :value))
                         v))
        normalize-value (fn [v]
                          ;; Keywords should match how you stored them in flat.* (usually without leading colon)
                          (if (keyword? v)
                            (if-let [ns (namespace v)]
                              (str ns "/" (name v))
                              (name v))
                            v))
        raw-vals (some->> values
                          (mapv unwrap-value))
        known? (boolean (some known-filter-option? raw-vals))
        ;; When using blanks in a set filter, AG Grid may include nil.
        blank? (boolean (some (fn [v]
                                (or (nil? v)
                                    (unknown-filter-option? v)))
                              raw-vals))
        data-vals (->> raw-vals
                       (remove nil?)
                       (remove known-filter-option?)
                       (remove unknown-filter-option?)
                       (mapv normalize-value))
        ;; Decide whether we should use .keyword for terms filtering.
        wants-keyword-field? (and (boolean (some string? data-vals))
                                  (not (string/ends-with? field ".keyword")))
        terms-field (if wants-keyword-field?
                      (str field ".keyword")
                      field)
        exists-field (if (string/ends-with? field ".keyword")
                       (string/replace field #"\.keyword$" "")
                       field)
        clauses (cond-> []
                  (seq data-vals)
                  (conj {:terms {terms-field data-vals}})

                  known?
                  (conj {:exists {:field exists-field}})

                  blank?
                  (conj {:bool {:must_not [{:exists {:field exists-field}}]}}))]
    (cond
      ;; nothing selected -> no clause
      (or (nil? raw-vals) (empty? raw-vals))
      nil

      ;; known and unknown together -> every row matches
      (and known? blank?)
      nil

      (= 1 (count clauses))
      (first clauses)

      (seq clauses)
      {:bool {:should               clauses
              :minimum_should_match 1}}

      :else
      nil)))

(defn mark-local-day-date-filters
  "`filter-model` with `:local-day? true` in the filter of each datetime column.
   A datetime column shows the day in the time zone of the browser, so its date
   filter must match the day in that time zone (see `date-filter->os-clause`). A
   date column shows the day in UTC, and its filter is not changed.
   `column-defs` are the columns of the grid, with `:colId` and `:field-data-type`."
  [filter-model column-defs]
  (let [datetime-col-ids (->> column-defs
                              (filter (fn [{:keys [field-data-type]}]
                                        (= :data.type/datetime field-data-type)))
                              (map :colId)
                              set)]
    (->> filter-model
         (map (fn [[col-id column-filter]]
                [col-id (cond-> column-filter
                          (contains? datetime-col-ids (name col-id))
                          (assoc :local-day? true))]))
         (into {}))))

(defn filter-model->os-clauses
  "Convert ag-grid filter model into a seq of OpenSearch clauses.

  - `filter-model` is a map from OS field -> filter model.
  - `accessible-fields` is a set of OS field strings that caller is allowed to use."
  [filter-model _accessible-fields]
  (->> filter-model
       (keep (fn [[field fm]]
               (if-let [os-field (resolve-filter-field field)]
                 (column-filter->os-clause os-field fm)
                 (logging/warn "Dropping filter on a field with no flat/labels representation"
                               field))))
       vec))

(defn sort-model->os-clauses
  [sort-model column-defs]
  (let [text-sort-data-types #{:data.type/text
                               :data.type/text-simple
                               :data.type/text-url
                               :data.type/email}
        exact-sort-data-types #{:data.type/keyword
                                :data.type/currency-symbol}
        date-sort-data-types #{:data.type/date
                               :data.type/datetime
                               :data.type/date-year}]
    (->> sort-model
         (mapcat (fn [sort-item]
                   (let [col-id (:colId sort-item)
                         order (:sort sort-item)
                         col-def (->> column-defs
                                      (cu/filter-by :colId col-id)
                                      (first))
                         data-type (:field-data-type col-def)
                         name-column? (:name-column? col-def)
                         field-path (or (:field-path col-def)
                                        (:field col-def))
                         sort-options (cond-> {:order order}
                                        (or name-column?
                                            (contains? exact-sort-data-types data-type)
                                            (contains? text-sort-data-types data-type)
                                            (contains? os-fields/ref-data-types data-type))
                                        (assoc :unmapped_type "keyword")

                                        (= :data.type/boolean data-type)
                                        (assoc :unmapped_type "boolean")

                                        (contains? date-sort-data-types data-type)
                                        (assoc :unmapped_type "date")

                                        (contains? #{:data.type/integer
                                                     :data.type/long
                                                     :data.type/id
                                                     :data.type/date-year}
                                                   data-type)
                                        (assoc :unmapped_type "long")

                                        (contains? #{:data.type/number
                                                     :data.type/percentage
                                                     :data.type/multiplier
                                                     :data.type/currency-value}
                                                   data-type)
                                        (assoc :unmapped_type "double"))
                       ;; A text value sorts without regard to case, with the
                       ;; case-sensitive field as the second key.
                         sort-fields (cond
                                       (contains? os-fields/ref-data-types data-type)
                                       (os-utils/text-sort-fields (os-utils/field-path->labels-alias field-path))

                                       (contains? exact-sort-data-types data-type)
                                       (os-utils/text-sort-fields (os-utils/field-path->labels-alias field-path))

                                       (contains? text-sort-data-types data-type)
                                       (os-utils/text-sort-fields (os-utils/field-path->flat-alias field-path))

                                       (= :data.type/boolean data-type)
                                       [(os-utils/field-path->flat-alias field-path)]

                                       :else
                                       [(os-utils/field-path->flat-alias field-path)])
                         ;; A field that shows the days since a time sorts on the
                         ;; time in the reverse order. A row with no time comes last.
                         since-field (days-since/since-alias (os-utils/field-path->flat-alias field-path))]
                     (if since-field
                       [{since-field {:order         (days-since/sort-order order)
                                      :unmapped_type "long"
                                      :missing       "_last"}}]
                       (mapv (fn [sort-field]
                               {sort-field sort-options})
                             sort-fields)))))
         vec)))

(defn request-sort
  "The sort of a row request of the grid. A column that the user sorted comes
   first, also when there is a search. The relevance of the search only orders the
   rows that the column does not tell apart. Without a sorted column, a search
   ranks by relevance."
  [{:keys [external-query-text sort-model column-defs]}]
  (listing-definition/sort-with-relevance
   external-query-text
   (when sort-model
     (sort-model->os-clauses sort-model column-defs))))

(rf/reg-event-fx
 ::load-data
 (fn [{db :db} [_ grid-id {:keys [entity-type end-row start-row sort-model os-fields column-defs
                                  pipeline-id filter-model row-group-cols group-keys _load-callback _fail-js] :as args}]]
   (let [allowed-os-fields (vec (or os-fields []))
         external-filters-by-field-path (get-in db (model/external-filters-full-path grid-id))
         external-filter-context (get-in db (model/external-filter-context-full-path grid-id))
         external-query-text (get-in db (model/external-query-text-full-path grid-id))
         query-field-paths (->> (concat allowed-os-fields
                                        (row-group-field-paths row-group-cols column-defs))
                                distinct
                                vec)
         query-context (assoc (or external-filter-context {})
                              :entity-type
                              entity-type
                              :query-field-paths
                              query-field-paths)
         group-value-mapping (get-in db (model/group-value-mapping-full-path grid-id))
         parent-group-clauses (row-group-parent-clauses row-group-cols group-keys column-defs group-value-mapping)
         request-queries (concat []
                                 (listing-definition/toolbar-filter-clauses external-filters-by-field-path
                                                                            external-filter-context)
                                 (listing-definition/toolbar-query-clauses external-query-text
                                                                           query-context)
                                 (when filter-model
                                   (filter-model->os-clauses (mark-local-day-date-filters filter-model column-defs)
                                                             allowed-os-fields))
                                 parent-group-clauses)
         grouped-level? (and (seq row-group-cols)
                             (< (count group-keys) (count row-group-cols)))
         group-column-def (active-row-group-column-def row-group-cols group-keys column-defs)
         group-field-path (active-row-group-field-path row-group-cols group-keys column-defs)
         column-totals (grid-totals/request-totals (visible-column-defs column-defs))
         request-body (cu/remove-nil-vals {:entity-type        entity-type
                                           :opensearch-queries request-queries
                                           ;; The totals of all the rows, on the first page only.
                                           :totals             (when (and (zero? (or start-row 0))
                                                                          (empty? group-keys)
                                                                          (seq column-totals))
                                                                 column-totals)
                                           :sort               (request-sort {:external-query-text external-query-text
                                                                              :sort-model          sort-model
                                                                              :column-defs         column-defs})
                                           :fields             allowed-os-fields
                                           :highlight-fields   (when-not (string/blank? external-query-text)
                                                                 (some-> (listing-definition/title-query-field entity-type)
                                                                         (vector)))
                                           :size               (- end-row start-row)
                                           :from               start-row
                                           :pipeline-id        pipeline-id})]
     (if (and grouped-level? (some? group-field-path))
       (let [;; A new query of the top level drops the totals of all the older
             ;; queries, also of the first page of the grid before it was grouped.
             ;; The next blocks of the same groups are the same query.
             first-block? (and (empty? group-keys)
                               (zero? (or start-row 0)))
             {db :db totals-id :id} (if first-block?
                                      (start-totals-request db grid-id)
                                      {:db db})
             facet-request-body (cu/remove-nil-vals {:entity-type        entity-type
                                                     :field-path         group-field-path
                                                     :facet-mode         (group-column-facet-mode group-column-def)
                                                     :limit              10000
                                                     :pipeline-id        pipeline-id
                                                     :opensearch-queries request-queries
                                                     ;; The totals of each group.
                                                     :totals             (not-empty column-totals)})
             query-key (query-results-event/request->query-key {:source :grid-grouped
                                                                :request facet-request-body})
             cache-args (assoc args
                               :grid-id grid-id
                               :query-key query-key
                               :request facet-request-body
                               :refresh-event [::trigger-query-search grid-id])]
         {:db (query-results-event/record-loading db query-key {:source :grid-grouped
                                                                :request facet-request-body
                                                                :refresh-event [::trigger-query-search grid-id]})
          ::ajax/send {:operation  :search/facets
                       :body       facet-request-body
                       :timeout-ms 60000
                       :on-ok      [::load-group-rows-ok grid-id cache-args group-field-path]
                       :on-error   [::load-data-failed cache-args]}
          ;; The groups do not give the count of the records or the totals of all
          ;; the rows: an average of averages is not the average. A search of all
          ;; the rows gives them.
          :fx         (when first-block?
                        [(clear-total-row-fx db grid-id)
                         [:dispatch [::load-grand-totals grid-id (cond-> {:id                 totals-id
                                                                          :entity-type        entity-type
                                                                          :pipeline-id        pipeline-id
                                                                          :opensearch-queries (vec request-queries)
                                                                          :fields             allowed-os-fields
                                                                          :column-defs        column-defs}
                                                                   (seq column-totals)
                                                                   (assoc :totals column-totals))]]])})
       (let [;; The first page sets the count of the rows and the totals. Only the
             ;; answer to the last query of the grid may set them.
             first-page? (and (zero? (or start-row 0))
                              (empty? group-keys))
             {db :db first-page-id :id} (if first-page?
                                          (start-totals-request db grid-id)
                                          {:db db})
             query-key (query-results-event/request->query-key {:source :grid
                                                                :request request-body})
             cache-args (assoc args
                               :grid-id grid-id
                               :first-page-id first-page-id
                               :query-key query-key
                               :request request-body
                               :refresh-event [::trigger-query-search grid-id])
             loading-db (query-results-event/record-loading db query-key {:source :grid
                                                                          :request request-body
                                                                          :refresh-event [::trigger-query-search grid-id]})]
         {:db (if (record-last-query? {:db db
                                       :grid-id grid-id
                                       :group-keys group-keys})
                (assoc-in loading-db
                          (model/last-query-full-path grid-id)
                          {:request-queries (vec request-queries)
                           :fields          allowed-os-fields
                           :entity-type     entity-type
                           :pipeline-id     pipeline-id})
                loading-db)
          :fx         (when first-page?
                        [(clear-total-row-fx db grid-id)])
          ::ajax/send {:operation  :search/search
                       :body       request-body
                       :timeout-ms 60000
                       :on-ok      [::load-data-ok grid-id cache-args]
                       :on-error   [::load-data-failed cache-args]}})))))

(rf/reg-event-fx
 ::get-filter-values-ok
 (fn [{db :db} [_ grid-id on-success-fn resp]]
    ;; Backend returns {:body {:buckets [{:value "..." :count N :label "..."} ...]}}
    ;; For AG Grid Set Filter we return a list of JS objects so UI can show labels
    ;; while still filtering by the stable underlying value.
   (let [buckets (get-in resp [:body :buckets])
         values (->> buckets
                     (mapv (fn [{:keys [value label count]}]
                             {:value value
                              :label label
                              :count count})))
         still-mounted? (boolean (get-in db (model/ag-grid-api-full-path grid-id)))]
     (when still-mounted?
       (on-success-fn values)))))

(rf/reg-event-fx
 ::get-filter-values-failed
 (fn [_ _]
   (js/console.error "Failed to load filter facets")
   {}))

(rf/reg-event-fx
 ::get-filter-values
 (fn [{db :db} [_ {:keys [entity-type field grid-id on-success-fn]}]]
   (let [grid-state (get-in db (model/grid-path grid-id))
         external-filters-by-field-path (get-in db (model/external-filters-full-path grid-id))
         external-filter-context (get-in db (model/external-filter-context-full-path grid-id))
         external-query-text (get-in db (model/external-query-text-full-path grid-id))
         filter-model (get grid-state model/filters-key)
         filter-model-without-target (without-target-column-filter filter-model field)
         config (get grid-state model/config-key)
         pipeline-id (get grid-state model/pipeline-key)
         {:keys [columns hidden-columns]} config
         query-field-paths (->> (or columns [])
                                (remove (set hidden-columns))
                                vec)
         query-context (assoc (or external-filter-context {})
                              :query-field-paths
                              query-field-paths)]
     {::ajax/send {:operation  :search/facets
                   :body       {:entity-type        entity-type
                                :field-path         field
                                :pipeline-id        pipeline-id
                                :opensearch-queries (concat []
                                                            (listing-definition/toolbar-filter-clauses external-filters-by-field-path
                                                                                                       external-filter-context)
                                                            (listing-definition/toolbar-query-clauses external-query-text
                                                                                                      query-context)
                                                            (when filter-model-without-target
                                                              (filter-model->os-clauses filter-model-without-target columns)))}
                   :timeout-ms 60000
                   :on-ok      [::get-filter-values-ok grid-id on-success-fn]
                   :on-error   [::get-filter-values-failed]}})))

(rf/reg-event-fx
 ::add-filter
 (fn [{_db :db} [_ _os-field]]
   {}))

(defn default-config
  [entity-type {:keys [pipeline-id accessible-field-paths]}]
  (let [entity-schema (registry/get-schema @session/tenant-schema entity-type)
        root-field-attributes (set (keys entity-schema))
        valid-single-root-field-path? (fn [field-path]
                                        (let [segments (when (sequential? field-path) (vec field-path))
                                              leaf (last segments)]
                                          (or (not= 1 (count segments))
                                              (contains? root-field-attributes leaf))))
        normalize-field-path (fn [field-path]
                               (cond
                                 (vector? field-path) field-path
                                 (keyword? field-path) [field-path]
                                 (sequential? field-path) (vec field-path)
                                 :else nil))
        po-default-columns (listing-definition/po-default-field-paths entity-type)
        accessible-field-paths (if (seq accessible-field-paths)
                                 (->> accessible-field-paths
                                      (keep normalize-field-path)
                                      (remove technical-or-derived-field-path?)
                                      (filter valid-single-root-field-path?)
                                      distinct
                                      vec)
                                 (->> (os-access/accessible-dedicated-os-fields
                                       {:tenant                      @session/tenant
                                        :tenant-schema               @session/tenant-schema
                                        :account                     @session/account
                                        :role                        @session/role
                                        :account-current-permissions (:account/current-permissions @session/account)
                                        :pipeline-id                 pipeline-id
                                        :entity-type                 entity-type})
                                      (keep :field-path)
                                      (keep normalize-field-path)
                                      (remove technical-or-derived-field-path?)
                                      (filter valid-single-root-field-path?)
                                      distinct
                                      vec))
        accessible-field-paths-set (set accessible-field-paths)
        schema-columns (->> entity-schema
                            (keep (fn [[field-kw _]]
                                    (let [field-path (normalize-field-path field-kw)]
                                      (when (contains? accessible-field-paths-set field-path)
                                        field-path))))
                            distinct
                            vec)
        extra-columns (->> accessible-field-paths
                           (remove (set schema-columns))
                           vec)
        accessible-columns (vec (concat schema-columns extra-columns))
        accessible-columns-set (set accessible-columns)
        visible-columns (if (seq po-default-columns)
                          (->> po-default-columns
                               (filter (fn [field-path]
                                         (contains? accessible-columns-set field-path)))
                               vec)
                          accessible-columns)
        hidden-columns (->> accessible-columns
                            (remove (set visible-columns))
                            (set))
        sorted-by [{:field-path "db/id"
                    :direction  :asc}]]
    {:columns        accessible-columns
     :hidden-columns hidden-columns
     :sorted-by      sorted-by}))

(defn- ls-path
  [grid-id & more]
  (into [(:db/id @session/account)
         (:db/id @session/tenant)
         :opensearch-grid-view-settings
         grid-id]
        more))

(defn- saved-view-payload
  [config]
  (select-keys config [:columns :hidden-columns :sorted-by :column-widths]))

(defn- saved-view-record
  [config]
  {:schema-version 1
   :scope/type :user
   :scope/id (:db/id @session/account)
   :view-mode :grid
   :updated-at-ms (.now js/Date)
   :payload (saved-view-payload config)})

(defn persisted-config
  "The config that the user saved for the grid in the local storage, or nil."
  [grid-id]
  (let [saved-view (ls/get-in (ls-path grid-id :saved-view))
        legacy-config (ls/get-in (ls-path grid-id :config))
        saved-payload (:payload saved-view)]
    (cond
      (map? saved-payload)
      saved-payload

      (map? legacy-config)
      legacy-config

      :else
      nil)))

(defn persisted-density
  "The density that the user saved for the grid in the local storage, or the default
   density. The density is saved apart from the columns and the sort: it is not part of
   a saved view."
  [grid-id]
  (grid-density/normalize-density (ls/get-in (ls-path grid-id :density))))

(defn column-state->sorted-by
  "The sort of the grid in the shape of the config: `[{:field-path .. :direction
   :asc|:desc}]`, in the order of the sort of the columns. `column-state` is the
   column state of AG Grid after `js->clj` with keywords as keys. `alias->field-path`
   gives the field path of a column from its colId. A column that is not sorted, or
   that has no field path, is not in the result."
  [column-state alias->field-path]
  (->> column-state
       (keep (fn [{:keys [colId sortIndex] sort-direction :sort}]
               (when-let [field-path (get alias->field-path colId)]
                 (when-let [direction (case sort-direction
                                        "asc" :asc
                                        "desc" :desc
                                        nil)]
                   {:field-path field-path
                    :direction  direction
                    :sort-index (or sortIndex 0)}))))
       (sort-by :sort-index)
       (mapv (fn [entry]
               (dissoc entry :sort-index)))))

(defn column-state->column-widths
  "The widths of the columns in the shape of the config: a map from the field path
   of a column to its width in pixels. `column-state` is the column state of AG
   Grid after `js->clj` with keywords as keys. `alias->field-path` gives the field
   path of a column from its colId. A column that has no field path, or no width
   that is a positive number, is not in the result."
  [column-state alias->field-path]
  (->> column-state
       (keep (fn [{:keys [colId width]}]
               (when-let [field-path (get alias->field-path colId)]
                 (when (and (number? width)
                            (pos? width))
                   [field-path width]))))
       (into {})))

(defn- sanitize-sorted-by
  "The entries of a saved sort that are still valid: the column exists, and the
   direction is known. A sort that an older version saved has another shape. It is
   dropped."
  [sorted-by columns]
  (let [column-set (set columns)]
    (->> sorted-by
         (filter (fn [entry]
                   (and (map? entry)
                        (contains? column-set (:field-path entry))
                        (contains? #{:asc :desc} (:direction entry)))))
         vec)))

(defn- sanitize-column-widths
  "The entries of saved column widths that are still valid: the column exists, and
   the width is a positive number. A value that is not a map gives no widths."
  [column-widths columns]
  (let [column-set (set columns)]
    (->> (when (map? column-widths)
           column-widths)
         (filter (fn [[field-path width]]
                   (and (contains? column-set field-path)
                        (number? width)
                        (pos? width))))
         (into {}))))

(defn sort-column-props
  "The saved sort of the config as props of the columns of AG Grid: a map from the
   field path of a column to its `:initialSort` and `:initialSortIndex`. AG Grid
   reads these two props when it creates the column, and ignores them after that,
   so a sort that the user changes later is not applied again. They leave the other
   state of the column as it is. The grid option `initialState` does not: with it,
   AG Grid ignores `hide` of every column."
  [sorted-by columns]
  (->> (sanitize-sorted-by sorted-by columns)
       (map-indexed (fn [index {:keys [field-path direction]}]
                      [field-path {:initialSort      (name direction)
                                   :initialSortIndex index}]))
       (into {})))

(defn config-sorted-by
  "The sort of a grid config that the grid can use: the entries for columns that
   the config has. The default sort of a config is by the id, which is not a
   column, so it is not in the result."
  [config]
  (sanitize-sorted-by (:sorted-by config) (:columns config)))

(defn config-with-sort
  "`config` with the sort `sorted-by`. An entry for a column that the config has
   not is dropped. The sorted columns are shown: they leave the hidden columns, so
   that the user sees what the rows are sorted by. An empty `sorted-by` clears the
   sort."
  [{:keys [config sorted-by]}]
  (let [sorted-by (sanitize-sorted-by sorted-by (:columns config))
        sorted-field-paths (set (map :field-path sorted-by))]
    (-> config
        (assoc :sorted-by sorted-by)
        (update :hidden-columns (fn [hidden-columns]
                                  (set (remove sorted-field-paths hidden-columns)))))))

(defn column-state-for-sort
  "The column state that gives AG Grid the sort `sorted-by`. Each sorted column
   has its sort, its place in the sort, and is not hidden. Every other column
   loses its sort."
  [sorted-by]
  {:state        (vec (map-indexed (fn [index {:keys [field-path direction]}]
                                     {:colId     (os-utils/field-path->alias field-path)
                                      :sort      (name direction)
                                      :sortIndex index
                                      :hide      false})
                                   sorted-by))
   :defaultState {:sort nil}})

(defn- sanitize-grid-config
  [entity-type _pipeline-id default-config incoming-config]
  (let [default-columns (vec (or (:columns default-config) []))
        default-column-set (set default-columns)
        mandatory-columns (set (listing-definition/grid-mandatory-field-paths entity-type))
        persisted-columns (->> (or (:columns incoming-config) [])
                               (filter (fn [field-path]
                                         (contains? default-column-set field-path)))
                               distinct
                               vec)
        persisted-column-set (set persisted-columns)
        merged-columns (->> (concat persisted-columns default-columns)
                            distinct
                            vec)
        persisted-hidden-columns (->> (or (:hidden-columns incoming-config) #{})
                                      (filter (fn [field-path]
                                                (contains? default-column-set field-path))))
        new-default-hidden-columns (->> (:hidden-columns default-config)
                                        (filter (fn [field-path]
                                                  (and (contains? default-column-set field-path)
                                                       (not (contains? persisted-column-set field-path))))))
        hidden-columns (->> (concat persisted-hidden-columns new-default-hidden-columns)
                            (remove (fn [field-path]
                                      (contains? mandatory-columns field-path)))
                            set)
        persisted-sorted-by (sanitize-sorted-by (:sorted-by incoming-config) default-columns)
        persisted-column-widths (sanitize-column-widths (:column-widths incoming-config) default-columns)]
    (cond-> (-> default-config
                (assoc :columns merged-columns)
                (assoc :hidden-columns hidden-columns))
      (seq persisted-sorted-by)
      (assoc :sorted-by persisted-sorted-by)

      (seq persisted-column-widths)
      (update :column-widths merge persisted-column-widths))))

(defn resolve-config
  "The config that a grid starts with: the default config, then the config that
   the caller gives, then the config that the user saved. Each step keeps only
   what the grid can use."
  [{:keys [entity-type pipeline-id accessible-field-paths config saved-config]}]
  (let [default-grid-config (default-config entity-type
                                            {:pipeline-id            pipeline-id
                                             :accessible-field-paths accessible-field-paths})
        base-config (if-not (cu/blank-value? config)
                      (sanitize-grid-config entity-type pipeline-id default-grid-config config)
                      default-grid-config)]
    (if (map? saved-config)
      (sanitize-grid-config entity-type pipeline-id base-config saved-config)
      base-config)))

(rf/reg-cofx
 ::persisted-grid-state
 (fn [coeffects]
   (let [[_ {:keys [grid-id]}] (:event coeffects)]
     (assoc coeffects :persisted-grid-state {:config  (persisted-config grid-id)
                                             :density (persisted-density grid-id)}))))

(defn init-fx
  "The effects of `::init`. The state that the user saved for the grid is in the local storage,
   so the coeffect `::persisted-grid-state` reads it: `:persisted-grid-state` is a map with
   `:config` (the saved config, or nil) and `:density`. The handler reads nothing else, so the
   same event, db and coeffects always give the same result."
  [{db :db :keys [persisted-grid-state]} [_ {:keys [grid-id entity-type pipeline-id filters config
                                                    external-filters-by-field-path external-filter-context
                                                    external-query-text accessible-field-paths]}]]
  (let [config (resolve-config {:entity-type             entity-type
                                :pipeline-id             pipeline-id
                                :accessible-field-paths  accessible-field-paths
                                :config                  config
                                :saved-config            (:config persisted-grid-state)})]
    {:db (-> db
             (assoc-in (model/entity-type-full-path grid-id) entity-type)
             (assoc-in (conj (model/grid-path grid-id) model/accessible-field-paths-key) accessible-field-paths)
             (assoc-in (model/filters-full-path grid-id) filters)
             ;; The grid gives a saved width to a column only when it makes the column
             ;; (`initialWidth`), so the widths of the start are kept apart from the
             ;; widths that the user changes later.
             (assoc-in (model/config-full-path grid-id) (assoc config
                                                               :density (:density persisted-grid-state)
                                                               :initial-column-widths (or (:column-widths config) {})))
             (assoc-in (model/pipeline-full-path grid-id) pipeline-id)
             (assoc-in (model/external-filters-full-path grid-id) (or external-filters-by-field-path {}))
             (assoc-in (model/external-filter-context-full-path grid-id) (or external-filter-context {}))
             (assoc-in (model/external-query-text-full-path grid-id) (or external-query-text "")))}))

(rf/reg-event-fx
 ::init
 [(rf/inject-cofx ::persisted-grid-state)]
 init-fx)

(rf/reg-event-fx
 ::grid-ready
 (fn [{db :db} [_ grid-id api]]
   {:db (assoc-in db (model/ag-grid-api-full-path grid-id) api)}))

(rf/reg-fx
 ::redraw-grid-rows
 (fn [api]
   (when (oops/oget+ api "redrawRows")
     (oops/ocall! api "redrawRows"))))

(defn redraw-rows-fx
  "Ask AG Grid to redraw the rows, so that `rowClassRules` runs again, for example when the record
   of the preview changes. The handler only returns the effect `::redraw-grid-rows` with the API
   of the grid. It returns no effect when the grid has no API yet."
  [{:keys [db]} [_ grid-id]]
  (if-let [api (get-in db (model/ag-grid-api-full-path grid-id))]
    {::redraw-grid-rows api}
    {}))

(rf/reg-event-fx
 ::redraw-rows
 redraw-rows-fx)

(rf/reg-event-fx
 ::clear-grid-selection
 (fn [{db :db} [_ grid-id]]
   (when-let [api (get-in db (model/ag-grid-api-full-path grid-id))]
     (when (oops/oget+ api "deselectAll")
       (oops/ocall! api "deselectAll")))
   {}))

(defn fetch-all-ids-fx
  "Request the ids of every row of the query that the grid shows now. The
   request is the last top-level query of the grid, so it matches the rows that
   the user sees. It asks for one field only, because the hits carry the id.
   `on-ok` gets the vector of ids as its last argument. `on-error` gets an error
   map."
  [{db :db} [_ grid-id {:keys [on-ok on-error]}]]
  (let [{:keys [request-queries fields entity-type pipeline-id]} (get-in db (model/last-query-full-path grid-id))
        total (get-in db (model/last-query-total-full-path grid-id))]
    (if (and (seq fields)
             (some? total))
      {::ajax/send {:operation  :search/search
                    :body       (cu/remove-nil-vals {:entity-type        entity-type
                                                     :opensearch-queries request-queries
                                                     :sort               []
                                                     :fields             [(first fields)]
                                                     :size               total
                                                     :from               0
                                                     :pipeline-id        pipeline-id})
                    :timeout-ms 60000
                    :on-ok      [::fetch-all-ids-ok {:on-ok on-ok}]
                    :on-error   on-error}}
      {:fx [[:dispatch (conj on-error {:message "The grid has not loaded its rows yet."})]]})))

(rf/reg-event-fx ::fetch-all-ids fetch-all-ids-fx)

(defn fetch-all-ids-ok-fx
  [_ [_ {:keys [on-ok]} resp]]
  {:fx [[:dispatch (conj on-ok (selection/hits->ids resp))]]})

(rf/reg-event-fx ::fetch-all-ids-ok fetch-all-ids-ok-fx)

(rf/reg-event-fx
 ::destroy
 (fn [{:keys [db]} [_ grid-id]]
   (let [api (get-in db (model/ag-grid-api-full-path grid-id))
         destroy-fn (oops/oget+ api "destroy")]
     (when (fn? destroy-fn)
       (oops/ocall! api "destroy"))
     {:db (update-in db model/root-path dissoc grid-id)})))

(def ^:private default-column-width
  "The width of a column that has no saved width. The columns of the grid set no
   width, so AG Grid gives each column its default width of 200 pixels."
  200)

(defn reset-column-state
  "The column state that a reset of the grid applies: no sort, and the default
   width for each column of data. A column of AG Grid itself, such as the column
   of the selection checkboxes, has a colId that starts with \"ag-Grid-\", and
   keeps its width. `column-state` is the column state of AG Grid after `js->clj`
   with keywords as keys.

   The grid gives a saved width to a column only when it makes the column, so a
   reset must set the widths itself. Before, a column that the user made wider
   stayed wide after the reset."
  [column-state]
  {:state        (->> column-state
                      (remove (fn [{:keys [colId]}]
                                (string/starts-with? (str colId) "ag-Grid-")))
                      (mapv (fn [{:keys [colId]}]
                              {:colId colId
                               :width default-column-width
                               :sort  nil})))
   :defaultState {:sort nil}})

(defn clear-saved-grid-state!
  "Clear what the user saved for the grid: the config, the saved view and the density.
   AG Grid keeps the filter, the sort and the widths of the columns itself, so a grid
   that has an API gets them cleared too."
  [{:keys [grid-id ag-api]}]
  (ls/set-in (ls-path grid-id :config) nil)
  (ls/set-in (ls-path grid-id :saved-view) nil)
  (ls/set-in (ls-path grid-id :density) nil)
  (when (and ag-api (oops/oget+ ag-api "setFilterModel"))
    (oops/ocall! ag-api "setFilterModel" nil))
  (when (and ag-api (oops/oget+ ag-api "applyColumnState"))
    (oops/ocall! ag-api "applyColumnState"
                 (-> (oops/ocall! ag-api "getColumnState")
                     (js->clj :keywordize-keys true)
                     reset-column-state
                     clj->js))))

(rf/reg-fx
 ::clear-saved-grid-state
 clear-saved-grid-state!)

(defn reset-config-fx
  "The effects of a reset of the grid: the default config goes to the db, the saved state
   and the state of AG Grid are cleared by an effect, and the query runs again."
  [{:keys [db]} [_ grid-id]]
  (if-let [entity-type (get-in db (model/entity-type-full-path grid-id))]
    (let [pipeline-id (get-in db (model/pipeline-full-path grid-id))
          accessible-field-paths (get-in db (conj (model/grid-path grid-id) model/accessible-field-paths-key))
          config (default-config entity-type
                                 {:pipeline-id             pipeline-id
                                  :accessible-field-paths accessible-field-paths})]
      {:db                       (-> db
                                     (assoc-in (model/config-full-path grid-id) config)
                                     (assoc-in (model/filters-full-path grid-id) nil)
                                     (assoc-in (model/group-value-mapping-full-path grid-id) {}))
       ::clear-saved-grid-state {:grid-id grid-id
                                 :ag-api  (get-in db (model/ag-grid-api-full-path grid-id))}
       :fx                       [[:dispatch [::trigger-query-search grid-id]]]})
    {:db db}))

(rf/reg-event-fx ::reset-config reset-config-fx)

(def inject-persisted-config
  "An interceptor that adds `:persisted-config` to the coeffects: the config that
   the user saved for the grid of the event. The event is `[id {:grid-id ..}]`."
  (rf/->interceptor
   :id ::inject-persisted-config
   :before (fn [context]
             (let [[_ {:keys [grid-id]}] (get-in context [:coeffects :event])]
               (assoc-in context [:coeffects :persisted-config] (persisted-config grid-id))))))

(rf/reg-fx
 ::persist-density
 (fn [{:keys [grid-id density]}]
   (ls/set-in (ls-path grid-id :density) (name density))))

(defn set-density-fx
  "Set the density of the grid in the config, and save it for the user. A value that is
   not a density gives the default density."
  [{:keys [db]} [_ grid-id density]]
  (let [density (grid-density/normalize-density density)]
    {:db               (assoc-in db (conj (model/config-full-path grid-id) :density) density)
     ::persist-density {:grid-id grid-id
                        :density density}}))

(rf/reg-event-fx ::set-density set-density-fx)

(rf/reg-fx
 ::persist-config
 (fn [{:keys [grid-id config]}]
   ;; A full local storage throws. Catch the error, so that it does not stop
   ;; the other effects and events of re-frame. The config in the db stays.
   (try
     (ls/set-in (ls-path grid-id :config)
                (saved-view-payload config))
     (ls/set-in (ls-path grid-id :saved-view)
                (saved-view-record config))
     (catch :default e
       (js/console.warn "Failed to persist grid config" e)))))

(rf/reg-fx
 ::apply-column-state
 (fn [{:keys [api column-state]}]
   (when (and api (oops/oget+ api "applyColumnState"))
     (oops/ocall! api "applyColumnState" (clj->js column-state)))))

(defn apply-filter-model!
  "Give the column filters to the grid. `filter-model` is a model as the grid
   sends it to `on-filter-model-changed`, with string keys. A model that is nil
   or empty does nothing, so the grid keeps its own filters. The grid sends the
   filter event for the new filters, and loads its rows again with them."
  [{:keys [api filter-model]}]
  (when (and api (seq filter-model))
    (oops/ocall! api "setFilterModel" (clj->js filter-model))))

(rf/reg-fx ::apply-filter-model apply-filter-model!)

(defn apply-sorted-by-fx
  "Handler of `::apply-sorted-by`. Give the grid a sort, and show the sorted
   columns. A grid that is open gets the sort at once. A grid that is not open
   (the listing is in the board view, or the mode changes to the grid in this same
   moment) gets it when it is made, because the sort is saved in the config of the
   user. The config of a grid that is not open is made as the grid makes it. The
   event gives the data that the grid needs for this: `entity-type`,
   `pipeline-id` and `accessible-field-paths`."
  [{:keys [db persisted-config]} [_ {:keys [grid-id sorted-by entity-type pipeline-id accessible-field-paths]}]]
  (let [open-config (get-in db (model/config-full-path grid-id))
        api (get-in db (model/ag-grid-api-full-path grid-id))
        base-config (or open-config
                        (resolve-config {:entity-type            entity-type
                                         :pipeline-id            pipeline-id
                                         :accessible-field-paths accessible-field-paths
                                         :saved-config           persisted-config}))
        next-config (config-with-sort {:config base-config :sorted-by sorted-by})]
    (cond-> {:fx [[::persist-config {:grid-id grid-id
                                     :config  next-config}]]}
      open-config
      (assoc :db (assoc-in db (model/config-full-path grid-id) next-config))

      api
      (update :fx conj [::apply-column-state {:api          api
                                              :column-state (column-state-for-sort (:sorted-by next-config))}]))))

(rf/reg-event-fx
 ::apply-sorted-by
 [inject-persisted-config]
 apply-sorted-by-fx)

(defn- column-alias->field-path
  "A map from the colId of each column of `columns` to its field path."
  [columns]
  (->> columns
       (map (fn [field-path]
              [(os-utils/field-path->alias field-path)
               field-path]))
       (into {})))

(defn column-resized-fx
  "The effects of `::column-resized`. `column-state` is the column state of AG Grid
   after `js->clj` with keywords as keys, after the user resized a column. The
   widths go into the config and into the local storage. The rows do not load
   again: the columns of the grid are made from the config without the widths (see
   `::ag-grid-col-defs`), so a new width does not render the grid."
  [{db :db} [_ grid-id column-state]]
  (when-let [config (get-in db (model/config-full-path grid-id))]
    (let [column-widths (column-state->column-widths column-state
                                                     (column-alias->field-path (:columns config)))
          next-config (assoc config :column-widths column-widths)]
      {:db (assoc-in db (model/config-full-path grid-id) next-config)
       :fx [[::persist-config {:grid-id grid-id
                               :config  next-config}]]})))

(rf/reg-event-fx ::column-resized column-resized-fx)

(rf/reg-event-fx
 ::columns-changed
 (fn [{db :db} [_ grid-id params-js]]
   (let [api (oops/oget+ params-js "api")
         column-state (-> (oops/ocall! api "getColumnState")
                          (js->clj :keywordize-keys true))
         current-config (get-in db (model/config-full-path grid-id))
         current-columns (vec (or (:columns current-config) []))
         alias->field-path (column-alias->field-path current-columns)
         entity-type (get-in db (model/entity-type-full-path grid-id))
         mandatory-columns (set (listing-definition/grid-mandatory-field-paths entity-type))
         persisted-columns (->> (or column-state [])
                                (keep (fn [{:keys [colId]}]
                                        (get alias->field-path colId)))
                                vec)
         merged-columns (->> (concat persisted-columns current-columns)
                             distinct
                             vec)
         hidden-columns (->> (or column-state [])
                             (filter (fn [{:keys [hide]}]
                                       (true? hide)))
                             (keep (fn [{:keys [colId]}]
                                     (get alias->field-path colId)))
                             (remove (fn [field-path]
                                       (contains? mandatory-columns field-path)))
                             set)
         ;; The search `_source` holds only the fields of the visible columns
         ;; (see `request-field-paths`), so a reload is needed when a column is
         ;; shown that adds a field, but not when one is hidden. Gating on the set
         ;; of visible fields growing - rather than on any visibility change - drops
         ;; the hide-side reloads and collapses the burst of column events one
         ;; show/hide emits into a single refetch.
         old-visible-columns (into #{}
                                   (remove (set (or (:hidden-columns current-config) #{})))
                                   current-columns)
         new-visible-columns (into #{} (remove hidden-columns) merged-columns)
         fields-grew? (boolean (some (fn [field-path]
                                       (not (contains? old-visible-columns field-path)))
                                     new-visible-columns))
         fields-changed? (not= old-visible-columns new-visible-columns)
         ;; The toolbar search and the column filters match on the fields of the
         ;; visible columns (`query-field-paths` and `allowed-os-fields` in
         ;; `load-data`), so a hide can change which rows match. Reload a hide too
         ;; while a search or a column filter is active.
         search-scope-active? (or (not (string/blank?
                                        (or (get-in db (model/external-query-text-full-path grid-id)) "")))
                                  (boolean (seq (or (get-in db (model/filters-full-path grid-id)) {}))))
         reload? (or fields-grew?
                     (and fields-changed? search-scope-active?))
         sorted-by (column-state->sorted-by column-state alias->field-path)
         next-config (-> current-config
                         (assoc :columns merged-columns)
                         (assoc :hidden-columns hidden-columns)
                         (assoc :sorted-by sorted-by)
                         (assoc :column-widths (column-state->column-widths column-state alias->field-path)))]
     {:db (assoc-in db (model/config-full-path grid-id) next-config)
      :fx (cond-> [[::persist-config {:grid-id grid-id
                                      :config  next-config}]]
            reload?
            (conj [:dispatch-later
                   {:ms 10
                    :dispatch ^:flush-dom [::trigger-query-search grid-id]}]))})))
